// BarChart — vertical column chart (bar height ∝ value), sorted descending, built
// on Recharts. Generic: items supply value/color/label + an optional topLabel
// above each bar; the hover tooltip body comes from renderTip(item) and renders
// in the shared `.chart-tip` card. Theming is token-driven (bar fills from the
// item color, axes/grid via var(--…)). Hover dim/brighten lives in bar-chart.css.
//
// A bar may be COMPOSED: give an item `segments` and it draws as a stack of
// hairline-separated parts sized by their share of the bar, rather than one solid
// block — so a basket of things reads as a basket without needing a click.
// Hovering or clicking a segment reports that segment, not the bar. `subLabel`
// adds a second, quieter line under the x-axis tick. Both are optional and mean
// nothing to the chart itself; the caller decides what a segment is.
//
// DENSE LINEUPS. Past 8 bars the labels stop fitting side by side, so the axis
// switches to the design's dense mode: ticks rotate −60° off the baseline and
// shrink, the bars tighten, and only the leading `topLabelLimit` bars keep their
// value label — the tail is short enough that a label would collide with its
// neighbour, and hover still names every bar. A 27-fund lineup reads cleanly
// instead of turning its axis into a smear.
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer, BarChart as RBarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, ReferenceLine, LabelList,
} from 'recharts';
import { useMediaQuery } from '../../../hooks';
import { ChartTooltip } from './ChartTooltip';
import { useTipHold } from './useTipHold';
import './bar-chart.css';

// % label above each bar (Recharts LabelList content render-prop).
function TopLabel({ x, y, width, value, index, limit, dense }) {
  // Bars are sorted descending, so "past the limit" is the small tail — where a
  // label would sit on top of its neighbour's. Those bars rely on hover instead.
  if (value == null || x == null || (limit != null && index >= limit)) return null;
  return (
    <text x={x + width / 2} y={y - 6} textAnchor="middle" fill="var(--text-1)" fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize={dense ? '9.5' : '11'} letterSpacing="-0.02em">
      {value}
    </text>
  );
}

// X-axis tick. Angled −60° when space is tight — on mobile, or once the lineup is
// dense — so a 5-character ticker stays readable where a horizontal one would
// overlap its neighbour. An item with a `subLabel` gets a second, quieter line
// beneath it, which only fits under a horizontal tick.
function XTick({ x, y, payload, mobile, dense, subLabels }) {
  const sub = !mobile && !dense && subLabels ? subLabels[payload.value] : null;
  // Angled rather than vertical: −60° keeps a 5-character ticker readable while
  // still fitting ~27 of them across the axis.
  const angled = mobile || dense;
  const ty = angled ? y + 8 : y + 14;
  return (
    <>
      <text
        x={x}
        y={ty}
        textAnchor={angled ? 'end' : 'middle'}
        transform={angled ? `rotate(-60 ${x} ${ty})` : undefined}
        fill="var(--text-2)"
        fontFamily="Plus Jakarta Sans"
        fontWeight="700"
        fontSize={dense ? '8.5' : '9'}
        letterSpacing={angled ? '0.5' : '1'}
      >
        {payload.value}
      </text>
      {sub && (
        <text x={x} y={y + 26} textAnchor="middle" fill="var(--text-3)" fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="8" letterSpacing="0.6">
          {sub}
        </text>
      )}
    </>
  );
}

// Top corners rounded, base square on the axis — the cap for a whole bar, or for
// the topmost segment of a composed one.
const capPath = (x, y, w, h, r0 = 4) => {
  const r = Math.max(0, Math.min(r0, h, w / 2));
  return (
    `M ${x},${y + r} A ${r},${r} 0 0 1 ${x + r},${y} H ${x + w - r} ` +
    `A ${r},${r} 0 0 1 ${x + w},${y + r} V ${y + h} H ${x} Z`
  );
};

// Gap between stacked segments, in px — enough to read as separate parts without
// swallowing a small one.
const SEG_GAP = 2.5;

// Smallest height a non-zero bar draws at, in px. A 1%-of-max holding is ~1px tall
// against a $1.5M lineup — a dotted smear along the baseline that can't be seen or
// hovered — so every NON-zero bar is floored here; a true zero stays flat. Module-level
// so the identity is stable: Recharts 3 stores bar settings in its own store and an
// inline arrow (new every render) keeps it re-syncing and the bars never draw.
const MIN_BAR_PX = 5;
const minBarSize = (value) => (value > 0 ? MIN_BAR_PX : 0);

/**
 * Bar body. A plain item draws one rounded column; an item with `segments` draws
 * the same column divided top-down by each segment's share of the value, so the
 * stack still measures the bar's total.
 */
function BarShape(props) {
  const { x, y, width, height, payload, onSegmentEnter, onSegmentLeave, onSegmentClick } = props;
  const segments = payload?.segments;
  if (!segments || !segments.length || !(height > 0)) {
    return <path d={capPath(x, y, width, height)} fill={payload?.color} />;
  }
  const total = segments.reduce((sum, sg) => sum + (sg.value || 0), 0) || 1;
  let cursor = y;
  return (
    <>
      {segments.map((sg, i) => {
        const raw = ((sg.value || 0) / total) * height;
        const top = cursor;
        const h = Math.max(raw - SEG_GAP, 2);
        cursor += raw;
        return (
          <path
            key={sg.key}
            d={i === 0 ? capPath(x, top, width, h) : `M ${x},${top} H ${x + width} V ${top + h} H ${x} Z`}
            fill={sg.color}
            onMouseEnter={() => onSegmentEnter?.(sg)}
            onMouseLeave={() => onSegmentLeave?.()}
            onClick={(e) => {
              e.stopPropagation();
              onSegmentClick?.(sg);
            }}
          />
        );
      })}
    </>
  );
}

export function BarChart({
  items, maxValue, ticks, formatTick, onSelect, renderTip, interactiveTip = false,
  denseAfter = 8, topLabelLimit = 10, maxBarSize = 140, minBandWidth = 30,
  ariaLabel = 'Bar chart',
}) {
  const mobile = useMediaQuery('(max-width: 600px)');
  // Tooltip is driven by the bar rectangle's own hover (not Recharts' category
  // band), so it only shows over the colored bar — never the empty space above it.
  // Hover records only WHAT is hovered; ChartTooltip places itself beside the
  // pointer, so no geometry needs plumbing through here.
  const [hover, setHover] = useState(null); // { key }
  const [hoverSeg, setHoverSeg] = useState(null);
  const data = [...items].sort((a, b) => b.value - a.value);
  const dense = data.length > denseAfter;

  // Recharts passes the bar datum with pixel geometry + payload; only identity is used.
  const onBarEnter = (d) => {
    if (!d) return;
    setHoverSeg(null);
    setHover({ key: d.payload?.key ?? d.key });
  };

  // A hovered segment describes itself, not the bar it sits in.
  const onSegEnter = (seg) => {
    setHoverSeg(seg);
    setHover({ key: seg.key });
  };

  const clearHover = () => {
    setHover(null);
    setHoverSeg(null);
  };

  // With an interactive card, leaving a bar only SCHEDULES the close — the pointer
  // needs those few frames to reach the card, which sits clear of it. Without one,
  // scheduleClose closes immediately, exactly as this chart always did.
  const { scheduleClose, cancelClose, closeNow } = useTipHold(clearHover, interactiveTip && !!renderTip);

  // x-axis label → its second line, for composed bars.
  const subLabels = data.reduce((acc, d) => {
    if (d.subLabel) acc[d.label] = d.subLabel;
    return acc;
  }, {});

  const hovered = hoverSeg || (hover ? data.find((d) => d.key === hover.key) : null);

  // Leaving the chart clears the hover as a backstop: Recharts fires mouseleave per
  // rectangle, which a fast diagonal exit can skip, leaving the card up. It goes
  // through the grace period, not straight to a close: the card overhangs the chart,
  // so the walk to its button crosses this very boundary. Once the pointer is inside
  // the card, ChartTooltip holds it open on its own.
  // SCROLL INSTEAD OF SHAVE. A category band is the stage width divided by the item
  // count, so a 22-fund lineup on a phone draws 9px bars under a smear of ticks — the
  // chart is present but unreadable. The stage already scrolls horizontally below
  // 600px (holdings-viz.css, where the treemap uses the same device); this is the
  // width the chart ASKS for, so the bars keep a readable band and the participant
  // pans the lineup instead of squinting at it. It has to come from here rather than
  // the stylesheet because it scales with the bar COUNT, which CSS cannot know; the
  // breakpoint that consumes it stays in bar-chart.css. A stage wider than this
  // ignores it — a min-width under the available width is a no-op, so nothing above
  // the breakpoint moves.
  const minWidth = data.length * minBandWidth;

  return (
    <div
      className="bar-chart"
      style={{ '--bar-chart-min-w': `${minWidth}px` }}
      aria-label={ariaLabel}
      onMouseLeave={scheduleClose}
    >
      <ResponsiveContainer width="100%" height="100%">
        {/* The left gutter exists for the FIRST angled tick, not the plot: with the
            y-axis hidden (mobile) a -60 degree label runs left of its bar and the
            leading ticker lost its first character to the SVG edge. ~14px is the
            horizontal reach of a 5-character tick at that angle. Above mobile the
            y-axis's own 52px already provides it. */}
        <RBarChart data={data} margin={{ top: 30, right: 8, bottom: 4, left: mobile ? 14 : 0 }} barCategoryGap={dense ? '6%' : '10%'}>
          <CartesianGrid vertical={false} stroke="var(--border-subtle)" strokeDasharray="3 4" />
          <ReferenceLine y={0} stroke="var(--border)" strokeWidth={1.5} />
          <YAxis
            type="number"
            domain={[0, maxValue]}
            ticks={ticks}
            tickFormatter={formatTick}
            axisLine={false}
            tickLine={false}
            width={mobile ? 0 : 52}
            hide={mobile}
            tick={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 11, fontWeight: 500, fill: 'var(--text-2)' }}
          />
          <XAxis
            dataKey="label"
            type="category"
            interval={0}
            axisLine={false}
            tickLine={false}
            height={mobile || dense ? 62 : 40}
            tick={<XTick mobile={mobile} dense={dense} subLabels={subLabels} />}
          />
          <Bar
            dataKey="value"
            isAnimationActive={false}
            maxBarSize={maxBarSize}
            minPointSize={minBarSize}
            shape={
              <BarShape
                onSegmentEnter={onSegEnter}
                onSegmentLeave={scheduleClose}
                onSegmentClick={(seg) => onSelect?.(seg)}
              />
            }
            onClick={(d) => {
              if (!onSelect || !d) return;
              // Recharts puts the datum under d.payload (mirrors onBarEnter); pass
              // the full item so the contract matches renderTip(item).
              const key = d.payload?.key ?? d.key;
              const item = data.find((x) => x.key === key);
              // A composed bar has nothing of its own to select — its segments
              // handle their own clicks.
              if (item && !item.segments?.length) onSelect(item);
            }}
            onMouseEnter={(d) => {
              if (d?.payload?.segments?.length) return; // segments own their hover
              cancelClose();
              onBarEnter(d);
            }}
            onMouseLeave={scheduleClose}
          >
            {data.map((d) => <Cell key={d.key} fill={d.color} />)}
            <LabelList
              dataKey="topLabel"
              content={<TopLabel limit={dense ? topLabelLimit : null} dense={dense} />}
            />
          </Bar>
        </RBarChart>
      </ResponsiveContainer>

      {renderTip && (
        <ChartTooltip
          visible={!!hovered}
          onDismiss={closeNow}
          interactive={interactiveTip}
          onHold={cancelClose}
          onRelease={scheduleClose}
        >
          {hovered ? renderTip(hovered) : null}
        </ChartTooltip>
      )}
    </div>
  );
}

BarChart.propTypes = {
  /** Bars. value drives height; label is the x-axis tick; topLabel sits above each bar. */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
      label: PropTypes.string,
      topLabel: PropTypes.string,
      /** Second, quieter line under the x-axis tick. */
      subLabel: PropTypes.string,
      /** Draw the bar as a stack of parts instead of one block. */
      segments: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          value: PropTypes.number.isRequired,
          color: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  /** Bars past this count switch the axis to angled, tightened, dense mode. */
  denseAfter: PropTypes.number,
  /** In dense mode, how many leading bars keep their topLabel. */
  topLabelLimit: PropTypes.number,
  /**
   * Widest a single bar may draw, in px. A category band is the chart width divided
   * by the item count, so a one- or two-item chart otherwise paints a bar as wide as
   * the card — 974px on a single-investment plan once the chart went full width. The
   * default sits above the widest bar a full lineup produces (~122px at 8 items), so
   * it only ever bites on short lineups.
   */
  maxBarSize: PropTypes.number,
  /**
   * Narrowest a category band may get before the chart asks its (scrollable) stage
   * for more width, in px — see the note at the root element. Only bites below the
   * 600px breakpoint in bar-chart.css.
   */
  minBandWidth: PropTypes.number,
  maxValue: PropTypes.number.isRequired,
  ticks: PropTypes.arrayOf(PropTypes.number).isRequired,
  /** (tickValue) => string for the y-axis labels. */
  formatTick: PropTypes.func,
  onSelect: PropTypes.func,
  /** (item) => node — tooltip body shown on hover. */
  renderTip: PropTypes.func,
  /**
   * The tooltip holds a control the user must be able to reach. Makes the card
   * hit-testable and keeps it up while the pointer travels to it. Off by default:
   * a reporting-only card should never intercept a click aimed at the chart.
   */
  interactiveTip: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default BarChart;
